<template>
    <div class="p-6 max-w-3xl mx-auto">
        <h1 class="text-2xl font-bold ml-6 mb-6">Mon profil</h1>

        <!-- Infos utilisateur -->
        <div v-if="user" class="space-y-6">

            <!-- Informations actuelles -->
            <div class="bg-white shadow rounded-lg p-4">
                <h2 class="text-lg font-semibold mb-4">Informations actuelles</h2>
                <p><strong>Nom : </strong> {{ user.nom }}</p>
                <p><strong>Email : </strong> {{ user.email }}</p>
                <p><strong>Date d'inscription : </strong> {{ formatDate(user.created_at) }}</p>
                <p><strong>Statut abonnement : </strong>
                    <span v-if="user.abonnement" class="text-green-600 font-semibold">Actif</span>
                    <span v-else class="text-red-600 font-semibold">Inactif</span>
                </p>

                <!-- Bouton abonnement -->
                <div>
                    <!-- Si abonné et pas en cours d'annulation -->
                    <div v-if="user.abonnement && !user.subscription_current_period_end" class=" p-4 text-center">
                        <button @click="showUnsubscribeModal = true"
                            class="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700">
                            Se désabonner
                        </button>
                    </div>

                    <!-- Si abonnement en cours d'annulation -->
                    <div v-else-if="user.subscription_current_period_end" class=" p-4 text-center text-yellow-700">
                        Votre abonnement se terminera le {{ new
                            Date(user.subscription_current_period_end).toLocaleDateString() }}
                    </div>

                    <!-- Si pas abonné -->
                    <div v-else class=" p-4 text-center">
                        <router-link to="/subscribe"
                            class="bg-violet-600 text-white px-4 py-2 rounded hover:bg-violet-700">
                            S’abonner
                        </router-link>
                    </div>
                </div>


            </div>

            <!-- Formulaire de mise à jour -->
            <div class="bg-white shadow rounded-lg p-4">
                <h2 class="text-lg font-semibold mb-4">Mettre à jour mes informations</h2>

                <!-- Nom -->
                <div class="flex gap-2 mb-4 items-end">
                    <div class="flex-1">
                        <label for="nom" class="block mb-1 font-medium">Nom</label>
                        <input v-model="form.nom" id="nom" type="text" class="border rounded px-3 py-2 w-full">
                    </div>
                    <button @click="updateField('nom', form.nom)"
                        class="h-fit self-end bg-primary text-white px-3 py-2 rounded hover:bg-primary-dark">
                        Modifier
                    </button>
                </div>

                <!-- Email -->
                <div class="flex gap-2 mb-4 items-end">
                    <div class="flex-1">
                        <label for="email" class="block mb-1 font-medium">Email</label>
                        <input v-model="form.email" id="email" type="email" class="border rounded px-3 py-2 w-full">
                    </div>
                    <button @click="updateField('email', form.email)"
                        class="h-fit self-end bg-primary text-white px-3 py-2 rounded hover:bg-primary-dark">
                        Modifier
                    </button>
                </div>

                <!-- Mot de passe -->
                <div class="flex gap-2 mb-6 items-end">
                    <div class="flex-1">
                        <label for="password" class="block mb-1 font-medium">Nouveau mot de passe</label>
                        <input v-model="form.password" id="password" type="password"
                            class="border rounded px-3 py-2 w-full" placeholder="Laisser vide si inchangé">
                    </div>
                    <button @click="updateField('password', form.password)"
                        class="h-fit self-end bg-primary text-white px-3 py-2 rounded hover:bg-primary-dark">
                        Modifier
                    </button>
                </div>

                <!-- Bouton supprimer le compte -->
                <div class="text-center">
                    <button @click="deleteAccount" class="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700">
                        Supprimer mon compte
                    </button>
                </div>
            </div>


        </div>

        <div v-else class="text-center text-gray-500">Chargement...</div>

        <!-- Modal confirmation désabonnement -->
        <div v-if="showUnsubscribeModal"
            class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div class="bg-white rounded p-8 max-w-sm w-full text-center shadow-lg">
                <h2 class="text-2xl font-bold mb-4 text-primary">Confirmer le désabonnement</h2>
                <p class="mb-6 text-gray-700">
                    Êtes-vous sûr de vouloir annuler votre abonnement ?<br>
                    Cette action est irréversible.
                </p>
                <div class="flex justify-center gap-4">
                    <button @click="confirmUnsubscribe"
                        class="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700">
                        Oui, me désabonner
                    </button>
                    <button @click="showUnsubscribeModal = false"
                        class="bg-gray-300 px-4 py-2 rounded hover:bg-gray-400">
                        Annuler
                    </button>
                </div>
            </div>
        </div>
        <!-- Modal validation -->
        <div v-if="showSuccess" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div class="bg-white rounded p-8 max-w-sm w-full text-center shadow-lg" role="alert" aria-live="assertive">
                <h2 class="text-2xl font-bold mb-4 text-primary">Enregistré !</h2>
                <p class="mb-6 text-gray-700">
                    Vos changements ont été enregistrés avec succès.
                </p>
                <button @click="showSuccess = false"
                    class="bg-primary text-white px-4 py-2 rounded hover:bg-hover transition duration-200">
                    Fermer
                </button>
            </div>
        </div>

        <!-- Modal erreur -->
        <div v-if="showError" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div class="bg-white rounded p-8 max-w-sm w-full text-center shadow-lg" role="alert" aria-live="assertive">
                <h2 class="text-2xl font-bold mb-4 text-red-600">Erreur</h2>
                <p class="mb-6 text-gray-700">
                    {{ errorMessage }}
                </p>
                <button @click="showError = false"
                    class="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition duration-200">
                    Fermer
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'

const baseUrl = process.env.VUE_APP_BASE_URL
const token = localStorage.getItem('token')

const user = ref(null)
const form = ref({ nom: '', email: '', password: '' })
const showUnsubscribeModal = ref(false)
// Modals succès/erreur
const showSuccess = ref(false)
const showError = ref(false)
const errorMessage = ref("")


// Charger les infos utilisateur
const fetchUser = async () => {
    try {
        const res = await fetch(`${baseUrl}/api/users/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
        })
        if (res.ok) {
            const data = await res.json()
            user.value = data
            form.value.nom = data.nom
            form.value.email = data.email
        }

    } catch (error) {
        console.error('Erreur chargement utilisateur', error)
        errorMessage.value = "Impossible de charger vos informations."
        showError.value = true
    }
}

const updateField = async (field, value) => {
    if (field === 'password' && !value) {
        errorMessage.value = "Le mot de passe ne peut pas être vide."
        showError.value = true
        return
    }
    try {
        const res = await fetch(`${baseUrl}/api/users/me`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ field, value })
        });

        if (res.ok) {
            const updated = await res.json()
            user.value = updated.user
            showSuccess.value = true
        } else {
            const errorText = await res.text()
            console.error("Erreur backend :", errorText)
            errorMessage.value = "Erreur lors de la mise à jour du champ."
            showError.value = true
        }
    } catch (error) {
        console.error('Erreur updateField', error)
        errorMessage.value = "Erreur réseau ou serveur."
        showError.value = true
    }
    await fetchUser();
};

const deleteAccount = async () => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer votre compte ? Cette action est irréversible.")) {
        return;
    }

    try {
        const res = await fetch(`${baseUrl}/api/users/me`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        if (res.ok) {
            alert("Votre compte a été supprimé avec succès.");
            // Déconnexion locale
            localStorage.removeItem("token");
            user.value = null;
            window.location.href = "/"; // ou page login
        } else {
            const errorText = await res.text()
            console.error("Erreur backend :", errorText)
            errorMessage.value = "Erreur lors de la suppression du compte."
            showError.value = true
        }
    } catch (error) {
        console.error("Erreur deleteAccount", error)
        errorMessage.value = "Erreur réseau ou serveur."
        showError.value = true
    }
};


// Confirmer le désabonnement
const confirmUnsubscribe = async () => {
    try {
        const res = await fetch(`${baseUrl}/api/stripe/unsubscribe`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
        })
        if (res.ok) {
            showSuccess.value = true
            await fetchUser()
        } else {
            errorMessage.value = "Erreur lors du désabonnement."
            showError.value = true
        }
    } catch (error) {
        console.error('Erreur désabonnement', error)
        errorMessage.value = "Erreur réseau ou serveur."
        showError.value = true
    } finally {
        showUnsubscribeModal.value = false
    }
}


const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })
}

onMounted(fetchUser)
</script>
